import AuthMixin from '@/components/mixins/auth-mixin'
import PlayerMenu from './player-menu/player-menu-tmpl'
import PlayerSearchBar from './player-search-bar/player-search-bar-tmpl'
import TagSlider from '@/components/common/tag-slider/tag-slider-tmpl'
import EventBus from '@/components/mixins/event-bus-mixin'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import OnEnterMixin from '@/components/mixins/onenter-mixin'
import Draggable from 'vuedraggable'
import { mapState } from 'vuex'
import CONST from '@/globals/constants'
import _ from 'lodash'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'

export const POLLING_ID = 'player-status-polling'
export const POLLING_LONG_INTERVAL = 30
export const POLLING_SHORT_INTERVAL = 10

export default {
  name: 'playerPage',
  inject: [
    '$currentUser',
    '$utils',
    '$api',
    '$apiError',
    '$pollingManager'
  ],
  components: { PlayerMenu, PlayerSearchBar, TagSlider, Draggable },
  props: {
    data: {
      type: String,
      default () { return '' }
    },
    isActive: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    sortDraggableOptions () {
      return { animation: 250 }
    },
    ...mapState({
      storeUpdatedTags: state => state.tagsComp.updatedTags,
      storeApiRequests: state => state.apiRequests.data
    })
  },
  watch: {},
  mixins: [AuthMixin, UnsavedDataMixin, OnEnterMixin],
  data () {
    return {
      serialNumber: null,
      enableDeletePlayerAction: this.$currentUser.getRights().userPlayer.deletePlayer,
      playerList: {
        batchHistory: [],
        itemsToShow: [],
        nextFetch: {
          moreItems: false,
          exclusiveStartSerialNumber: null,
          nextToken: null,
        },
        reqFilter: { search: '' }
      },
      inlineEditName: {
        isInputVisible: false,
        serialNumber: null,
        previousVal: null,
        requesting: false
      },
      playerSelection: [],
      previousPlayerItemSelected: { playerItem: {}, playerItemPosition: 0 },
      previousKeyPressedInPlayerSelection: null,
      intervalInSeconds: POLLING_LONG_INTERVAL,
      refreshTagSlider: { ref: null, refresh: false },
      selectedTagsFromSlider: [],
      tagsSummary: [],
      hoverDeleteIcon: false,
      allLEDTags: [],
      configFileDragging: false,
      targetSerialNumberPlayerConfigDrop: null
    }
  },
  methods: {
    getPlayers (paramsSrc) {
      if (!paramsSrc) paramsSrc = {}

      const resetPlayersList = paramsSrc.resetList
      const additionalParams = paramsSrc.additionalRequestParams ? paramsSrc.additionalRequestParams : {}
      const callback = paramsSrc.callback ? paramsSrc.callback : false

      let params = { max: 30 }

      // Get search text value
      if (this.playerList.reqFilter.search !== '') params.search = this.playerList.reqFilter.search
      // Get selected tags
      if (this.selectedTagsFromSlider?.length) {
        params.tags = JSON.stringify(this.selectedTagsFromSlider)
      }

      // Merge additional request params
      if (additionalParams) {
        params = { ...params, ...additionalParams }
      }

      // Reset the player List
      if (resetPlayersList) this.handleResetPlayerList()

      const apiRequest = () => {
        if (!resetPlayersList) this.$utils.mainLoader(true)

        this.error = ''

        this.$api.get(`${CONFIG.API_BASE_URL}/player`, { params })
          .then(response => {
            const newPlayersBatch = response.data.players
            this.watchPlayerConfigDragAndDrop()

            const processNewbatch = () => {
              const playerList = this.$utils.clone(this.playerList)

              // Process newPlayersBatch
              if (newPlayersBatch.length > 0) {
                // Put the newPlayerBatch into batch history
                playerList.batchHistory.push(newPlayersBatch)

                // Add each new player item into itemsToShow
                playerList.itemsToShow.push(...newPlayersBatch)

                // Check the last player item if there's moreItems property
                const lastPlayerItem = newPlayersBatch[newPlayersBatch.length - 1]

                if (response.data.nextToken) {
                  playerList.nextFetch.moreItems = false
                  playerList.nextFetch.exclusiveStartSerialNumber = null
                  playerList.nextFetch.nextToken = response.data.nextToken
                } else if (lastPlayerItem.moreItems && lastPlayerItem.moreItems) {
                  playerList.nextFetch.moreItems = true
                  playerList.nextFetch.exclusiveStartSerialNumber = lastPlayerItem.serialNumber
                  playerList.nextFetch.nextToken = null
                } else {
                  playerList.nextFetch.moreItems = false
                  playerList.nextFetch.exclusiveStartSerialNumber = null
                  playerList.nextFetch.nextToken = null
                }

                this.playerList = this.filterPlayerList({ playerList })
                this.setIntervalPerSecond()
              }
            }

            if (resetPlayersList) {
              this.handleResetPlayerList()
              processNewbatch()
            } else if (paramsSrc.configUploaded) {
              const prevPlayerList = this.$utils.clone(this.playerList)
              const prevPlayersListToShow = prevPlayerList.itemsToShow
              const prevPlayerSelection = this.$utils.clone(this.playerSelection)

              this.handleResetPlayerList()
              processNewbatch()

              const newPlayerList = this.$utils.clone(this.playerList)
              let newPlayerSelection = []

              // Update items to show
              if (prevPlayersListToShow.length) {
                newPlayerList.itemsToShow = newPlayerList.itemsToShow.filter((player) => {
                  return prevPlayersListToShow.find((p) => { return player.serialNumber === p.serialNumber })
                })
              }

              // Update players selection
              if (prevPlayerSelection.length && newPlayerList.itemsToShow) {
                newPlayerSelection = newPlayerList.itemsToShow.filter((player) => {
                  return prevPlayerSelection.find((p) => { return player.serialNumber === p.serialNumber })
                })
              }

              this.playerSelection = newPlayerSelection
              this.playerList = this.filterPlayerList({ playerList: newPlayerList })
            } else {
              processNewbatch()
            }

            if (callback) callback()

            this.$utils.mainLoader(false)
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() }, showDialogNotification: true })
          })
      }
      apiRequest()
    },
    isDeviceTypeHMP300 (device) {
      return device.deviceType && device.deviceType === 'HMP300'
    },
    isDeviceTypeHMP350 (device) {
      return device.deviceType && device.deviceType === 'HMP350'
    },
    isDeviceTypeHMP400 (device) {
      return device.deviceType && device.deviceType === 'HMP400'
    },
    isDeviceTypeHMP400W (device) {
      return device.deviceType && device.deviceType === 'HMP400W'
    },
    isDeviceTypeiBX440 (device) {
      return device.deviceType && device.deviceType === 'iBX440'
    },
    isDeviceTypeDSOS (device) {
      return device.deviceType && device.deviceType === 'DSOS'
    },
    isDeviceTypeDiVA (device) {
      return device.deviceType && device.deviceType === 'DiVA'
    },
    isDeviceTypeiBX410 (device) {
      return device.deviceType && device.deviceType === 'iBX410'
    },
    isDeviceTypeiBX410W (device) {
      return device.deviceType && device.deviceType === 'iBX410W'
    },
    getAllLEDTags () {
      this.$api.get(`${CONFIG.API_BASE_URL}/group/tags/?type=ledTags,videowallTags`)
        .then(response => {
          const { ledTags, videowallTags } = response.data
          const mergedTags = _.union(ledTags ?? [], videowallTags ?? [])

          this.allLEDTags = this.$utils.filterTagOptionsForUI({ tags: mergedTags, showLabel: true, removeLEDPrefixForLabel: true })
        })
        .catch((error) => {
          this.$utils.mainLoader(false)
          this.$apiError.handleAPIRequestError(error, { originalRequest: () => { this.getAllLEDTags() }, showDialogNotification: true })
        })
    },
    getStatusClassnameIcon (player) {
      const okIcon = 'ok-icon'
      const warningIcon = 'warning-icon'
      const unconfiguredIcon = 'unconfigured-icon'
      const updatingIcon = 'updating-icon'
      const orangeIcon = 'orange-icon'
      const publish = 'publish-icon'
      let resIconClassname = okIcon

      if (player.status === 'disconnected') {
        resIconClassname = warningIcon
      } else if (player.status === 'unconfigured') {
        resIconClassname = unconfiguredIcon
      } else if (!player.online) {
        resIconClassname = warningIcon
      } else {
        if (player.status === 'unconfigured') {
          resIconClassname = unconfiguredIcon
        } else if (player.firmwareUpdate || player.configPending || player.degraded) {
          resIconClassname = orangeIcon
        } else if (player.status === 'publish') {
          resIconClassname = publish
        } else if (player.status === 'updating') {
          resIconClassname = updatingIcon
        }
      }

      return resIconClassname
    },
    registerPolling () {
      this.$pollingManager.register(
        POLLING_ID,
        async () => { await this.keepEachPlayerStatusUpdated() },
        this.intervalInSeconds
      )
    },
    deregisterPolling () {
      this.$pollingManager.deregister(POLLING_ID)
    },
    startPolling () {
      if (!this.$pollingManager.isRunning(POLLING_ID)) {
        this.$pollingManager.start(POLLING_ID)
      }
    },
    stopPolling () {
      this.$pollingManager.stop(POLLING_ID)
    },
    applyPlayerStatusUpdate (playerItem, updatedPlayerItem) {
      if (updatedPlayerItem.firmwareVersion) {
        playerItem.firmwareVersion = updatedPlayerItem.firmwareVersion
      }

      playerItem.status = updatedPlayerItem.status
      playerItem.online = updatedPlayerItem.online ? updatedPlayerItem.online : false

      if (playerItem.configPending && !updatedPlayerItem.configPending) {
        delete playerItem.configPending
      } else if (Object.prototype.hasOwnProperty.call(updatedPlayerItem, 'configPending')) {
        playerItem.configPending = updatedPlayerItem.configPending
      }

      if (playerItem.firmwareUpdate && !updatedPlayerItem.firmwareUpdate) {
        delete playerItem.firmwareUpdate
      } else if (Object.prototype.hasOwnProperty.call(updatedPlayerItem, 'firmwareUpdate')) {
        playerItem.firmwareUpdate = updatedPlayerItem.firmwareUpdate
      }
    },
    async keepEachPlayerStatusUpdated () {
      let queryStringParams = '?statusOnly=1'
      if (this.playerList.reqFilter.search !== '') {
        queryStringParams += `&search=${this.playerList.reqFilter.search}`
      }

      try {
        const response = await this.$api.get(`${CONFIG.API_BASE_URL}/player${queryStringParams}`)
        const responseData = response.data.players
        const playerList = this.$utils.clone(this.playerList)
        const playerSelection = this.$utils.clone(this.playerSelection)

        responseData.forEach((updatedPlayerItem) => {
          // Update Batch History
          for (const playerBatch of playerList.batchHistory) {
            for (const playerItemInBatch of playerBatch) {
              if (playerItemInBatch.serialNumber === updatedPlayerItem.serialNumber) {
                this.applyPlayerStatusUpdate(playerItemInBatch, updatedPlayerItem)
              }
            }
          }

          // Update Items to Show
          for (const playerItemToShow of playerList.itemsToShow) {
            if (playerItemToShow.serialNumber === updatedPlayerItem.serialNumber) {
              this.applyPlayerStatusUpdate(playerItemToShow, updatedPlayerItem)
            }
          }

          // Update player selection
          for (const selectedPlayer of playerSelection) {
            if (selectedPlayer.serialNumber === updatedPlayerItem.serialNumber) {
              this.applyPlayerStatusUpdate(selectedPlayer, updatedPlayerItem)
            }
          }
        })

        this.playerList = this.filterPlayerList({ playerList })
        this.playerSelection = playerSelection
      } catch (error) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'error',
          alert: {
            message: error.response ? error.response?.data.message ?? error.message : this.$t('errors.unexpectedError')
          }
        })

        // Stop polling on error
        this.stopPolling()
        // On 401 Unauthorized error, will handle the error
        // and re-start polling after token refresh
        this.$apiError.handleUnauthorizedError(error, {
          originalRequest: () => {
            this.startPolling()
          }
        })
      }
    },
    handlePlayerAddedEvent () {
      this.getPlayers({ resetList: true })
    },
    handleResetPlayerList () {
      const playerList = this.$utils.clone(this.playerList)

      playerList.itemsToShow = []
      playerList.batchHistory = []
      playerList.nextFetch = {
        moreItems: false,
        exclusiveStartSerialNumber: null,
        nextToken: null
      }
      this.playerList = this.filterPlayerList({ playerList })
    },
    handlePlayerCardSelection ($event, selectedPlayer, currentPlayerItemPosition) {
      const isCTRLKeyPressed = $event.ctrlKey
      const isShiftKeyPressed = $event.shiftKey

      // Process selection
      const processPlayerSelection = () => {
        let playerSelection = this.$utils.clone(this.playerSelection)
        const playerList = this.$utils.clone(this.playerList)

        // Check selected player if exist in current selection list
        const isPlayerExistInSelectionList = { exist: false, currentPlayerItemPosition: 0 }

        const findFromCurrentSelection = playerSelection.findIndex((item) => { return item.serialNumber === selectedPlayer.serialNumber })
        if (findFromCurrentSelection >= 0) {
          isPlayerExistInSelectionList.exist = true
          isPlayerExistInSelectionList.currentPlayerItemPosition = findFromCurrentSelection
        }

        // Check if player has pending API request form store
        if (!isPlayerExistInSelectionList.exist) {
          const hasPendingRequest = () => { return (this.storeApiRequests && this.storeApiRequests.length) ? this.storeApiRequests.find((r) => { return r.status === 'requesting' && r.originalData.serialNumber === selectedPlayer.serialNumber }) : false }
          let runInterval = null

          if (hasPendingRequest()) {
            this.$utils.mainLoader(true)

            runInterval = setInterval(() => {
              if (!hasPendingRequest()) {
                clearInterval(runInterval)
                this.$utils.mainLoader(false)
              }
            }, 100)
          }
        }

        // Keep the last player item selected
        const setLastPlayerItemClicked = () => {
          this.previousPlayerItemSelected = {
            playerItem: selectedPlayer,
            playerItemPosition: currentPlayerItemPosition
          }
        }

        // Single Selection No Key pressed
        if (!isCTRLKeyPressed && !isShiftKeyPressed) {
          playerSelection = []

          if (['shift', 'ctrl'].indexOf(this.previousKeyPressedInPlayerSelection) >= 0) {
            playerSelection.push(selectedPlayer)
          } else {
            if (!isPlayerExistInSelectionList.exist) {
              playerSelection.push(selectedPlayer)
            }
          }

          setLastPlayerItemClicked() // Keep as last media item clicked
          this.previousKeyPressedInPlayerSelection = null // Keep as previous key pressed
        }

        // Multiple Selection with CTRL Key Pressed
        if (isCTRLKeyPressed || (isCTRLKeyPressed && !isShiftKeyPressed)) {
          if (!isPlayerExistInSelectionList.exist) {
            playerSelection.push(selectedPlayer)
          } else {
            playerSelection.splice(isPlayerExistInSelectionList.currentPlayerItemPosition, 1)
          }

          setLastPlayerItemClicked() // Keep as last media item clicked
          this.previousKeyPressedInPlayerSelection = 'ctrl' // Keep as previous key pressed
        }

        // Multiple Selection with SHIFT Key Pressed
        if (isShiftKeyPressed) {
          const previousPlayerItemSelected = this.$utils.clone(this.previousPlayerItemSelected)
          const previousPlayerItemPosition = previousPlayerItemSelected.playerItemPosition

          playerSelection = []

          if (previousPlayerItemPosition < currentPlayerItemPosition) {
            for (let i = previousPlayerItemPosition; i <= currentPlayerItemPosition; i++) {
              playerSelection.push(this.playerList.itemsToShow[i])
            }
          }

          if (previousPlayerItemPosition > currentPlayerItemPosition) {
            for (let i = currentPlayerItemPosition; i <= previousPlayerItemPosition; i++) {
              playerSelection.push(this.playerList.itemsToShow[i])
            }
          }

          this.previousKeyPressedInPlayerSelection = 'shift' // Keep as previous key pressed
        }

        this.playerSelection = playerSelection
        this.playerList = this.filterPlayerList({ playerList, playerSelection })
        this.$store.dispatch('updatePlayerSelection', this.playerSelection)
      }

      if (this.playerSelection.length && (this.storeUpdatedTags.tags.length && this.storeUpdatedTags.lastAction !== null)) {
        setTimeout(() => {
          processPlayerSelection()
        }, 10)
      } else {
        processPlayerSelection()
      }
    },
    handlePlayerCardOnClick ($event, selectedPlayer, currentPlayerItemPosition) {
      this.checkUnsavedData({ nextAction: () => this.handlePlayerCardSelection($event, selectedPlayer, currentPlayerItemPosition) })
    },
    filterPlayerList (params) {
      const playerList = this.$utils.clone(params.playerList)
      const playerSelection = params.playerSelection ? this.$utils.clone(params.playerSelection) : this.$utils.clone(this.playerSelection)
      const filteredItemsToShow = []

      // Make sure no duplicate items to show
      this.$utils.clone(playerList.itemsToShow).forEach((playerItem) => {
        // Set _edit prop default values
        if (!playerItem._edit) {
          playerItem._edit = {
            selected: false,
            isHover: false,
            isActive: false
          }
        } else {
          playerItem._edit.selected = false
          playerItem._edit.isHover = false
        }

        // Check if present in selection list
        const findInSelectionList = playerSelection.findIndex((p) => { return p.serialNumber === playerItem.serialNumber }) >= 0

        playerItem._edit.isHover = false
        if (findInSelectionList) {
          playerItem._edit.selected = true
        }

        if (!filteredItemsToShow.find((i) => { return i.serialNumber === playerItem.serialNumber })) {
          filteredItemsToShow.push(playerItem)
        }
      })

      playerList.itemsToShow = filteredItemsToShow

      return playerList
    },
    updateOriginalParentComponentDataInSearchBar (actionType, playerItem) {
      const isSearchBarEnable = (typeof this.$refs.playerSearchBar === 'object') && (this.$refs.playerSearchBar.originalPresentPlayerListBeforeTheSearch !== null)

      if (!isSearchBarEnable) return // No need to udpate data from search bar

      const playerListInSearchBar = this.$utils.clone(this.$refs.playerSearchBar.originalPresentPlayerListBeforeTheSearch)

      if (actionType === 'delete') {
        playerListInSearchBar.batchHistory.forEach((playerByBatch) => {
          const updatedMediaByBatch = this.$utils.deleteItemFromList(playerByBatch, playerItem, 'serialNumber')
          playerByBatch = updatedMediaByBatch
        })

        const newPlayerListToShowInSearchBar = this.$utils.deleteItemFromList(playerListInSearchBar.itemsToShow, playerItem, 'serialNumber')
        playerListInSearchBar.itemsToShow = newPlayerListToShowInSearchBar
      }

      if (actionType === 'updateAllList') {
        playerListInSearchBar.batchHistory = this.playerList.batchHistory
        playerListInSearchBar.itemsToShow = this.playerList.itemsToShow
      }

      this.$refs.playerSearchBar.originalPresentPlayerListBeforeTheSearch = playerListInSearchBar
    },
    handlePlayerMenuOnChange (event) {
      if (event.action === 'PLAYER_SETUP_DATA_UPDATED') {
        // Notify tag slider to refresh with pre selected tags if available
        // but not including `ALL` tag
        const preSelectedTags = (this.selectedTagsFromSlider || [])
          .filter((tag) => tag !== CONST.ALL_TAG_VALUE)
          .map((tag) => ({ value: tag }))

        this.updatePlayerListData(event.payload.data.playerSelection)
        this.refreshTagSlider = {
          ref: this.$utils.generateUniqueID(),
          refresh: true,
          preSelectedTags
        }

        this.getPlayers({ resetList: true })
      }
    },
    updatePlayerListData (updatedPlayers) {
      const playerList = this.$utils.clone(this.playerList)
      // Update Player List in batch in history
      playerList.batchHistory.forEach((batch, batchIndx) => {
        for (const indx in batch) {
          const updatedData = updatedPlayers.find((data) => { return data.serialNumber === batch[indx].serialNumber })
          if (updatedData && updatedData.tags) batch[indx].tags = updatedData.tags // Update tags
          if (updatedData && updatedData.config) batch[indx].config = updatedData.config // Update config
          if (updatedData && updatedData.playTimes) batch[indx].playTimes = updatedData.playTimes // Update playTimes
        }
      })

      // Update players in itemsToShow
      playerList.itemsToShow.forEach((item) => {
        const updatedData = updatedPlayers.find((data) => { return data.serialNumber === item.serialNumber })

        if (updatedData && updatedData.tags) item.tags = updatedData.tags // Update tags
        if (updatedData && updatedData.config) item.config = updatedData.config // Update config
        if (updatedData && updatedData.playTimes) item.playTimes = updatedData.playTimes // Update playTimes
      })
      this.playerList = this.filterPlayerList({ playerList })

      // Update players in selection
      const playerSelection = this.$utils.clone(this.playerSelection)
      playerSelection.forEach((item) => {
        const updatedData = updatedPlayers.find((data) => { return data.serialNumber === item.serialNumber })
        if (updatedData && updatedData.tags) item.tags = updatedData.tags // Update tags
        if (updatedData && updatedData.config) item.config = updatedData.config // Update config
        if (updatedData && updatedData.playTimes) item.playTimes = updatedData.playTimes // Update playTimes
      })

      this.playerSelection = playerSelection
      this.updateOriginalParentComponentDataInSearchBar('updateAllList')
    },
    setIntervalPerSecond () {
      this.intervalInSeconds = POLLING_LONG_INTERVAL

      for (const indx in this.playerList.itemsToShow) {
        const player = this.playerList.itemsToShow[indx]

        if (['publish', 'ok'].indexOf(player.status) < 0) {
          this.intervalInSeconds = POLLING_SHORT_INTERVAL
          break
        }
      }

      // Re-register polling with updated interval
      const pollStatus = this.$pollingManager.getPollStatus(POLLING_ID)
      if (!pollStatus || pollStatus.intervalSeconds === this.intervalInSeconds) return

      const wasRunning = this.$pollingManager.isRunning(POLLING_ID)
      this.stopPolling()
      this.deregisterPolling()
      this.registerPolling()
      if (wasRunning) {
        this.$pollingManager.start(POLLING_ID)
      }
    },
    handleDeletePlayerIconClick () {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'warning',
        title: this.$t('confirm.deletePlayer'),
        descriptionI18n: {
          key: 'playerSetup.playerDeleteConfirmation',
          params: { playerName: this.playerSelection.map(p => p.name).join(', ') }
        },
        alert: {
          message: this.$t('playerSetup.playerDeleteWarning')
        },
        confirmButtonText: this.$t('buttons.deletePlayer'),
        confirmButtonIcon: 'flowbite:trash-bin-solid',
        callbacks: {
          eventOKButtonClick: () => {
            this.playerSelection.forEach((player, indx) => {
              const apiRequest = () => {
                this.$utils.mainLoader(true)

                this.$api.delete(`${CONFIG.API_BASE_URL}/player/${player.serialNumber}`)
                  .then(response => {
                    this.refreshTagSlider = { ref: this.$utils.generateUniqueID(), refresh: true }

                    const playerList = this.$utils.clone(this.playerList)
                    // Delete from Batch History
                    playerList.batchHistory.forEach((playersByBatch) => {
                      const updatedPlayersByBatch = this.$utils.deleteItemFromList(playersByBatch, { serialNumber: player.serialNumber }, 'serialNumber')
                      playersByBatch = updatedPlayersByBatch
                    })
                    // Delete From Items to show
                    const newItemsToShow = this.$utils.deleteItemFromList(playerList.itemsToShow, { serialNumber: player.serialNumber }, 'serialNumber')
                    playerList.itemsToShow = newItemsToShow
                    this.playerList = this.filterPlayerList({ playerList })

                    this.updateOriginalParentComponentDataInSearchBar('delete')

                    // Remove from selection
                    const updatePlayerSelection = this.$utils.clone(this.playerSelection)
                    updatePlayerSelection.forEach((selectedPlayer, selectedPlayerIndx) => {
                      if (selectedPlayer.serialNumber === player.serialNumber) updatePlayerSelection.splice(selectedPlayerIndx, 1)
                    })
                    this.playerSelection = updatePlayerSelection
                    this.$utils.mainLoader(false)
                  })
                  .catch((error) => {
                    this.$utils.mainLoader(false)
                    this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() }, showDialogNotification: true })
                  })
              }
              apiRequest()
            })
          }
        }
      })
    },
    /**
     * Search bar component onChange hook function
     */
    handlePlayerSearchBarChange (event) {
      if (event.action === 'NEW_PLAYER_SEARCH_RESULTS') {
        const data = event.payload.data
        const playerList = this.$utils.clone(this.playerList)
        const newSearchResults = data.searchResults

        playerList.itemsToShow = newSearchResults.fullDetailsList
        playerList.reqFilter = data.reqSearchFilter
        playerList.nextFetch = {
          moreItems: data.nextFetch?.moreItems ?? false,
          exclusiveStartSerialNumber: data.nextFetch?.exclusiveStartSerialNumber ?? null,
          nextToken: data.nextFetch?.nextToken ?? null
        }

        // Cleanup player selection
        let playerSelection = this.$utils.clone(this.playerSelection)
        playerSelection = playerSelection.filter((selectedPlayer) => {
          return playerList.itemsToShow.find((p) => { return p.serialNumber === selectedPlayer.serialNumber })
        })

        this.playerSelection = playerSelection
        this.playerList = this.filterPlayerList({ playerList })

        // After backend request final result
        if (data.afterAPIRequest) {
          if (!this.playerList.itemsToShow.length) {
            this.showNoSearchResults = true
          } else {
            this.showNoSearchResults = false
          }
        } else if (!data.afterAPIRequest && this.playerList.itemsToShow.length) {
          this.showNoSearchResults = false
        }
      }

      if (event.action === 'RESET_TO_ORIGINAL_LISTS') {
        const data = event.payload.data
        const prevDataBeforeSearch = this.$utils.clone(data.originalPlayerList)
        prevDataBeforeSearch.itemsToShow = []

        prevDataBeforeSearch.batchHistory.forEach((batch, batchIndx) => {
          batch.forEach((player, indx) => {
            prevDataBeforeSearch.itemsToShow.push(player)
          })
        })

        this.playerList = this.filterPlayerList({ playerList: prevDataBeforeSearch })
        this.showNoSearchResults = false
      }
    },
    handleTagSliderChange ($event) {
      const updatePlayerList = (params) => {
        const currentPlayerSelection = this.$utils.clone(this.playerSelection)
        const selectedTagsFromSlider = this.$utils.clone(this.selectedTagsFromSlider)
        let tagsSummary = this.$utils.clone(this.tagsSummary)

        this.$utils.mainLoader(true)
        this.getPlayers({
          resetList: true,
          callback: () => {
            // Update selected tags from slider
            if (params.updatedTag) {
              selectedTagsFromSlider.forEach((tag, indx) => {
                if (tag === $event.payload.data.prevVal) selectedTagsFromSlider[indx] = $event.payload.data.newVal
              })
            }

            // Delete selected tags from slider
            if (params.deletedTag) {
              tagsSummary = tagsSummary.filter((tag) => {
                return tag.value !== $event.payload.data.prevVal
              })
            }

            this.tagsSummary = tagsSummary
            this.playerSelection = currentPlayerSelection
            this.previousPlayerItemSelected = { playerItem: {}, playerItemPosition: 0 }
            this.previousKeyPressedInPlayerSelection = null
            this.selectedTagsFromSlider = selectedTagsFromSlider
            this.refreshTagSlider = { ref: this.$utils.generateUniqueID(), refresh: false }
            this.$utils.mainLoader(false)
          },
          refreshList: true
        })
      }

      if ($event.action === CONST.EMIT_EVENT_ALL_TAG_SELECTED_FROM_SLIDER) {
        this.selectedTagsFromSlider = []
        this.getPlayers({ resetList: true })
      }

      if ($event.action === CONST.EMIT_EVENT_NEW_SELECTED_TAGS_FROM_SLIDER) {
        this.selectedTagsFromSlider = $event.payload.data
      }

      if ($event.action === CONST.EMIT_EVENT_DONE_FETCHING_TAGS_FROM_SLIDER) {
        this.tagsSummary = $event.payload.data

        if (this.playerSelection.length && !$event.payload.data.length) {
          this.playerSelection = []
          updatePlayerList({ deletedTag: true })
        }
      }

      if ($event.action === CONST.EMIT_EVENT_UPDATING_TAG_FROM_SLIDER) {
        const prevTagVal = $event.payload.data.oldTagValue
        const newTagVal = $event.payload.data.newTagValue
        const newPlayerList = this.playerList

        // Update players by batch
        newPlayerList.batchHistory = newPlayerList.batchHistory.map(batch => {
          for (const player of batch) {
            player.tags = player.tags.map(tag => tag === prevTagVal ? newTagVal : tag)
          }
          return batch
        })

        // Update items to show data
        newPlayerList.itemsToShow = newPlayerList.itemsToShow.map(player => {
          player.tags = player.tags.map(tag => tag === prevTagVal ? newTagVal : tag)
          return player
        })

        // Update selected player data
        this.playerSelection = this.playerSelection.map(selectedPlayer => {
          const updatedData = newPlayerList.itemsToShow.find(p => p.serialNumber === selectedPlayer.serialNumber)
          if (!_.isEmpty(updatedData)) {
            selectedPlayer = updatedData
          }
          return selectedPlayer
        })

        this.playerList = this.filterPlayerList({ playerList: newPlayerList })
      }

      if ($event.action === CONST.EMIT_EVENT_DELETED_TAG_FROM_SLIDER) {
        if ($event.payload.data.fromRightSideMenu) {
          const prevTagVal = $event.payload.data.prevVal
          const newPlayerSelection = this.$utils.clone(this.playerSelection)

          for (const player of newPlayerSelection) {
            if (player.tags) {
              player.tags = player.tags.filter((playerTag) => {
                return playerTag !== prevTagVal
              })
            }
          }

          this.playerSelection = newPlayerSelection
        } else {
          this.playerSelection = []
          updatePlayerList({ deletedTag: true })
        }
      }
    },
    handleInlineEditNameClick (event, playerItem, itemPosition) {
      this.checkUnsavedData({
        nextAction: () => {
          // Check item if selected
          const isPlayerSelected = playerItem._edit && playerItem._edit.selected

          // If player is selected, then enable edit name field
          if (isPlayerSelected) {
            const inlineEditName = this.$utils.clone(this.inlineEditName)
            inlineEditName.isInputVisible = true
            inlineEditName.serialNumber = playerItem.serialNumber

            this.inlineEditName = inlineEditName
            this.$nextTick(_ => {
              this.$refs[`${playerItem.serialNumber}-editName`][0].$refs.input.focus()
              this.inlineEditName.previousVal = playerItem.name
            })
          }

          // If media is not selected, then set the media a selected
          if (!isPlayerSelected) {
            this.handlePlayerCardSelection(event, playerItem, itemPosition)
          }
        }
      })
    },
    handleInlineEditNameInputConfirm (playerItem, indx) {
      if (this.inlineEditName.previousVal === playerItem.name || this.inlineEditName.requesting) return

      const updatePlayer = () => {
        const requestData = { serialNumber: playerItem.serialNumber, name: playerItem.name }
        const inlineEditName = this.$utils.clone(this.inlineEditName)
        inlineEditName.requesting = true
        this.inlineEditName = inlineEditName

        this.$utils.mainLoader(true)
        this.$api.put(`${CONFIG.API_BASE_URL}/player/${playerItem.serialNumber}`, requestData)
          .then(response => {
            const inlineEditName = this.$utils.clone(this.inlineEditName)
            inlineEditName.isInputVisible = false
            inlineEditName.serialNumber = null
            inlineEditName.requesting = false
            this.inlineEditName = inlineEditName

            this.updatePlayerListData([response.data])
            this.$utils.mainLoader(false)
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { updatePlayer() }, showDialogNotification: true })
          })
      }
      updatePlayer()
    },
    handleUploadChange (file) {
      this.configFileDragging = false

      if (!file.raw.type.includes('text/xml')) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'error',
          alert: {
            message: this.$t('errors.fileNotSupported')
          }
        })
        return
      }

      this.$utils.mainLoader(true)
      this.$refs[`playerConfigFileUpload-${this.targetSerialNumberPlayerConfigDrop}`][0].submit()
    },
    handleUploadHttpRequest (fileList) {
      const selectedFile = fileList.file
      const reader = new FileReader()
      let readXml = null
      reader.onload = (e) => {
        readXml = e.target.result
        // let parser = new DOMParser();
        // let doc = parser.parseFromString(readXml, "application/xml")
        const serialNumber = this.targetSerialNumberPlayerConfigDrop.trim()
        const url = `${CONFIG.API_BASE_URL}/player/` + serialNumber + '/config'

        const apiRequest = () => {
          this.$api.put(url, readXml, {
            transformRequest: [(data, headers) => {
              headers['Content-Type'] = 'application/xml'
              return data
            }]
          })
            .then(response => {
              this.getPlayers({ configUploaded: true })
              this.configFileDragging = false
            })
            .catch((error) => {
              this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() }, showDialogNotification: true })
            })
        }
        apiRequest()
      }
      reader.readAsText(selectedFile)
    },
    watchPlayerConfigDragAndDrop () {
      $(window).on('dragenter', (el) => {
        this.configFileDragging = true
        const configUploadDragger = $(el.target).hasClass('el-upload-dragger') ? $(el) : false

        if (configUploadDragger) {
          const parentEl = $(el.target).offsetParent()
          const parentElId = $(parentEl).offsetParent().attr('id')
          this.targetSerialNumberPlayerConfigDrop = parentElId.substr(23)
        }
      })

      window.addEventListener('dragleave', (el) => {
        if (el.x === 0 && el.y === 0) {
          this.configFileDragging = false
        }
      })
    },
    handleWindowClicked () {
      $(window).on('click', (e) => {
        const targetEl = e.target ? $(e.target) : null

        // Reset player selection when clicking white area
        const isWhiteBackgroundClicked = targetEl && (targetEl.hasClass('workspace') || targetEl.hasClass('search-bar-content'))
        if (isWhiteBackgroundClicked) {
          this.checkUnsavedData({
            nextAction: () => {
              this.playerSelection = []
              this.playerList = this.filterPlayerList({ playerList: this.playerList })
            }
          })
        }
      })
    },
    onEnter (event) {
      if (event.target.id === 'player-edit-name-input') {
        event.target.blur()
      }
    },
    /**
     * Hook function for draggable as dropzone for `tags-shared` group
     *
     * @param {Object} event - VueDraggable add event
     * @param {Object} playerItem - Player item
     */
    handleTagDraggableAdd (event, playerItem) {
      const draggedEl = event.item // element being dragged
      const draggedDataset = draggedEl?.dataset // data-* attributes
      const dataDraggableType = draggedDataset?.draggableType ?? draggedEl.getAttribute('data-draggable-type') // 'tag' or 'popId'
      const dataDraggableValue = draggedDataset?.draggableValue ?? draggedEl.getAttribute('data-draggable-value') // tag or popId value

      if (!draggedEl || !dataDraggableType || !dataDraggableValue || !playerItem?.serialNumber) {
        return
      }

      const newTag = dataDraggableType === 'tag' ? dataDraggableValue : null
      const serialNumber = playerItem.serialNumber
      const getPlayerDetailsFromListToShow = this.$utils.getItemFromList(this.playerList.itemsToShow, 'serialNumber', serialNumber)

      if (newTag === CONST.ALL_TAG_VALUE) {
        return
      }

      const getPlayerDetails = () => {
        this.$utils.mainLoader(true)
        this.$api.get(`${CONFIG.API_BASE_URL}/player/${serialNumber}`)
          .then(response => {
            const putPayload = { serialNumber, tags: response.data.tags ? response.data.tags : [] }

            if (putPayload.tags.indexOf(newTag) < 0) {
              putPayload.tags.push(newTag)

              const updatePlayer = () => {
                this.$utils.mainLoader(true)
                this.$api.put(`${CONFIG.API_BASE_URL}/player/${serialNumber}`, putPayload)
                  .then(response => {
                    this.updatePlayerListData([response.data])

                    if (getPlayerDetailsFromListToShow.value && getPlayerDetailsFromListToShow.value._edit && !getPlayerDetailsFromListToShow.value._edit.selected) {
                      const currentPlayerItemIndx = parseInt(getPlayerDetailsFromListToShow.position)
                      this.handlePlayerCardSelection({}, this.playerList.itemsToShow[currentPlayerItemIndx], currentPlayerItemIndx)
                    }
                    this.$utils.mainLoader(false)
                  })
                  .catch((error) => {
                    this.$utils.mainLoader(false)
                    this.$apiError.handleAPIRequestError(error, { originalRequest: () => { updatePlayer() }, showDialogNotification: true })
                  })
              }
              updatePlayer()
            } else {
              this.$utils.mainLoader(false)
            }
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { getPlayerDetails() }, showDialogNotification: true })
          })
      }
      getPlayerDetails()
    }
  },
  beforeUnmount () {
    this.stopPolling()
    this.deregisterPolling()
  },
  mounted () {
    // Register polling for player status updates but not
    // starting it yet. It will be started after the first
    // fetch of players list.
    this.registerPolling()

    this.serialNumber = this.$route.params?.serialNumber ?? null

    // Fetch players list on scroll
    this.$utils.infiniteScroll('playerListContainer', () => {
      if (this.playerList.nextFetch.nextToken) {
        this.getPlayers({
          additionalRequestParams: {
            nextToken: this.playerList.nextFetch.nextToken
          }
        })
      } else if (this.playerList.nextFetch.moreItems) {
        this.getPlayers({
          additionalRequestParams: {
            exclusiveStartSerialNumber: this.playerList.nextFetch.exclusiveStartSerialNumber
          }
        })
      }
    })

    this.handleWindowClicked()
    this.removeUnsavedDataFlagInLocalStorage()

    // If Authenticated then get the players
    this.isAuthenticated({
      callback: () => {
        this.getPlayers({
          resetList: true,
          callback: () => {
            useMixpanel().track('Players')
            this.startPolling()
            this.$utils.updatePageComponentStyle({ name: 'playerPage' })
            this.$utils.mainLoader(false)
          },
          refreshList: true
        })
        this.getAllLEDTags()
      }
    })
  },
  beforeRouteLeave (_to, from, next) {
    // Check first for unsaved data before redirecting anywhere
    this.checkUnsavedData({ nextAction: () => next() })
  },
  beforeRouteUpdate (_to, from, next) {
    // Check first for unsaved data before redirecting anywhere
    this.checkUnsavedData({ nextAction: () => next() })
  }
}
